import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { FaEdit, FaTrash } from "react-icons/fa";
import { hasPermission, ROLES, FEATURES } from "../lib/rolesConfig";
import { commitFirestoreWrite, offlineAwareMessage } from "../utils/offline";

const NotesObservationsPage = () => {
  const { currentUser, activeCareProfile, userRole } = useAuth();
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [isLoadingNotes, setIsLoadingNotes] = useState(true);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [showNotesForm, setShowNotesForm] = useState(false);
  const rowRefs = useRef({});

  // Load More State
  const [visibleRecordsCount, setVisibleRecordsCount] = useState(5);
  const [sortOrder] = useState("desc");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const canWrite =
    activeCareProfile && userRole
      ? hasPermission(
          userRole[activeCareProfile.id] || ROLES.OWNER,
          FEATURES.NOTES_OBSERVATIONS,
          "write",
        )
      : false;
  const canRead =
    activeCareProfile && userRole
      ? hasPermission(
          userRole[activeCareProfile.id] || ROLES.OWNER,
          FEATURES.NOTES_OBSERVATIONS,
          "read",
        )
      : false;

  const toJsDate = (ts) => {
    if (!ts) return null;
    if (ts.toDate) return ts.toDate();
    return new Date(ts);
  };

  useEffect(() => {
    if (!canRead || !activeCareProfile) {
      setNotes([]);
      setIsLoadingNotes(false);
      return;
    }
    setIsLoadingNotes(true);
    const q = query(
      collection(db, "notesObservations"),
      where("careProfileId", "==", activeCareProfile.id),
      orderBy("createdAt", "desc"),
      limit(100),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setNotes(data);
        setIsLoadingNotes(false);
      },
      (err) => {
        console.error("Error listening notes:", err);
        setIsLoadingNotes(false);
      },
    );
    return () => unsub();
  }, [canRead, activeCareProfile]);

  const sortedNotes = React.useMemo(() => {
    return [...notes].sort((a, b) => {
      const dateA = toJsDate(a.lastModifiedAt || a.createdAt);
      const dateB = toJsDate(b.lastModifiedAt || b.createdAt);
      if (!dateA || !dateB) return 0;
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [notes, sortOrder]);

  const handleLoadMore = () => {
    setVisibleRecordsCount((prevCount) => prevCount + 5);
  };

  const resetForm = () => {
    setNewNote("");
    setIsEditingNote(false);
    setCurrentNoteId(null);
    setShowNotesForm(false);
  };

  const handleCancelEdit = () => {
    const idToScrollTo = currentNoteId;
    resetForm();
    setShowNotesForm(false);
    if (idToScrollTo && rowRefs.current[idToScrollTo]) {
      setTimeout(() => {
        rowRefs.current[idToScrollTo].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  const handleNoteChange = (e) => {
    setNewNote(e.target.value);
  };

  const formatNoteTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return "N/A";
    return timestamp.toDate().toLocaleString(); // e.g., 5/28/2024, 10:00:00 AM
  };

  const handleSubmitNote = async (e) => {
    e.preventDefault();
    if (!canWrite) {
      return alert("You don't have permission to save notes for this profile.");
    }
    if (!currentUser || !activeCareProfile) {
      window.alert(
        "User not authenticated or no active care profile selected. Cannot save note.",
      );
      return;
    }
    if (!newNote.trim()) {
      window.alert("Please enter a note.");
      return;
    }

    if (isEditingNote && currentNoteId) {
      // Update existing note
      try {
        setIsSubmittingNote(true);
        await commitFirestoreWrite(
          updateDoc(doc(db, "notesObservations", currentNoteId), {
            noteText: newNote,
            lastModifiedAt: Timestamp.now(),
            lastModifiedBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || currentUser.email,
            },
          }),
          "Update note",
        );
        window.alert(offlineAwareMessage("Note updated successfully!"));
        resetForm();
      } catch (error) {
        console.error("Error updating note: ", error);
        window.alert(`Failed to update note. ${error.message}`);
      } finally {
        setIsSubmittingNote(false);
      }
    } else {
      // Add new note
      try {
        setIsSubmittingNote(true);
        const noteRef = doc(collection(db, "notesObservations"));
        await commitFirestoreWrite(
          setDoc(noteRef, {
            noteText: newNote,
            careProfileId: activeCareProfile.id,
            userId: activeCareProfile.userId,
            createdAt: Timestamp.now(),
            createdBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || currentUser.email,
            },
            lastModifiedAt: Timestamp.now(),
            lastModifiedBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || currentUser.email,
            },
          }),
          "Create note",
        );
        window.alert(offlineAwareMessage("Note added successfully!"));
        resetForm();
        setShowNotesForm(false);
      } catch (error) {
        console.error("Error adding note: ", error);
        window.alert(`Failed to add note. ${error.message}`);
      } finally {
        setIsSubmittingNote(false);
      }
    }
  };

  const handleEditNote = (note) => {
    if (!canWrite) return alert("You don't have permission to edit notes.");
    setIsEditingNote(true);
    setCurrentNoteId(note.id);
    setNewNote(note.noteText);
    setShowNotesForm(true);
    setTimeout(() => {
      const formElement = document.getElementById("notes-form-section");
      if (formElement)
        formElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleDeleteNote = async (id) => {
    if (!canWrite) return alert("You don't have permission to delete notes.");
    if (!currentUser) {
      window.alert("User not authenticated. Cannot delete note.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this note?")) {
      try {
        setDeletingId(id);
        await commitFirestoreWrite(
          deleteDoc(doc(db, "notesObservations", id)),
          "Delete note",
        );
        window.alert(offlineAwareMessage("Note deleted successfully!"));
      } catch (error) {
        console.error("Error deleting note: ", error);
        window.alert(`Failed to delete note. ${error.message}`);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const getSubmitLabel = () => {
    if (isSubmittingNote) return "Saving...";
    return isEditingNote ? "Save Changes" : "Submit Note";
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center mb-6">
        <h1 className="text-4xl font-bold text-yellow-800 mb-4 md:mb-0">
          Notes & Observations
        </h1>
        {canWrite && (
          <button
            onClick={() => {
              if (isEditingNote || showNotesForm) {
                handleCancelEdit();
              } else {
                resetForm();
                setShowNotesForm(true);
                setTimeout(() => {
                  const formElement =
                    document.getElementById("notes-form-section");
                  if (formElement)
                    formElement.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                }, 100);
              }
            }}
            className={`px-6 py-2 text-white font-semibold rounded-lg shadow-md transition duration-300 ease-in-out ${
              isEditingNote || showNotesForm
                ? "bg-red-500 hover:bg-red-600"
                : "bg-yellow-500 hover:bg-yellow-600"
            }`}
          >
            {isEditingNote
              ? "Cancel Edit"
              : showNotesForm
                ? "Cancel"
                : "Add New Note"}
          </button>
        )}
      </div>

      {/* Add/Edit Note Form */}
      {(showNotesForm || isEditingNote) && (
        <div
          id="notes-form-section"
          className="mb-8 bg-white p-6 rounded-lg shadow-lg border-l-4 border-yellow-500"
        >
          <h3 className="text-2xl font-semibold text-gray-700 mb-4">
            {isEditingNote ? "Edit Note" : "Add a New Note"}
          </h3>
          <form onSubmit={handleSubmitNote}>
            <div className="mb-4">
              <label
                htmlFor="newNote"
                className="block text-sm font-medium text-gray-600 mb-2"
              >
                Observation Details
              </label>
              <textarea
                id="newNote"
                name="newNote"
                rows="5"
                value={newNote}
                onChange={handleNoteChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition"
                placeholder="E.g., Seemed more cheerful today, mentioned enjoying the morning walk..."
                required
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-6 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingNote}
                className={`px-6 py-2 font-semibold rounded-lg shadow-md transition w-full sm:w-auto ${isSubmittingNote ? "bg-gray-400 cursor-not-allowed" : "bg-yellow-500 text-white hover:bg-yellow-600"}`}
              >
                {getSubmitLabel()}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Notes History Table */}
      <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-yellow-500">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">History</h3>
        {isLoadingNotes ? (
          <div className="text-center py-4">
            <p className="text-gray-500">Loading observations...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-500">
              No observations have been recorded yet.
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Click "Add New Note" to get started.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {sortedNotes.slice(0, visibleRecordsCount).map((note) => (
                <div
                  key={note.id}
                  ref={(el) => (rowRefs.current[note.id] = el)}
                  className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 flex flex-col relative"
                >
                  {/* Action Buttons for Tablet/Desktop */}
                  <div className="hidden sm:flex items-center space-x-3 absolute top-4 right-4">
                    {canWrite && (
                      <>
                        <button
                          onClick={() => handleEditNote(note)}
                          className="text-yellow-600 hover:text-yellow-800 transition-colors"
                          title="Edit Note"
                        >
                          <FaEdit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={deletingId === note.id}
                          className={`transition-colors ${deletingId === note.id ? "text-gray-400 cursor-not-allowed" : "text-red-500 hover:text-red-700"}`}
                          title="Delete Note"
                        >
                          <FaTrash size={15} />
                        </button>
                      </>
                    )}
                  </div>
                  <p className="text-gray-700 flex-grow mb-4 whitespace-pre-wrap sm:pr-16">
                    {note.noteText}
                  </p>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
                    <div className="text-xs text-gray-500 italic">
                      {note.lastModifiedBy ? (
                        <span>
                          {note.lastModifiedBy.uid !== activeCareProfile.userId
                            ? `Last updated by ${note.lastModifiedBy.name} on `
                            : `Updated on `}
                          <span className="flex flex-col sm:inline">
                            <span>
                              {
                                formatNoteTimestamp(note.lastModifiedAt).split(
                                  ",",
                                )[0]
                              }
                              ,
                            </span>
                            <span className="sm:ml-1">
                              {
                                formatNoteTimestamp(note.lastModifiedAt).split(
                                  ",",
                                )[1]
                              }
                            </span>
                          </span>
                        </span>
                      ) : (
                        <span>
                          Created on
                          <span className="flex flex-col sm:inline">
                            <span>
                              {
                                formatNoteTimestamp(note.createdAt).split(
                                  ",",
                                )[0]
                              }
                              ,
                            </span>
                            <span className="sm:ml-1">
                              {
                                formatNoteTimestamp(note.createdAt).split(
                                  ",",
                                )[1]
                              }
                            </span>
                          </span>
                        </span>
                      )}
                    </div>
                    {/* Action Buttons for Mobile */}
                    <div className="flex sm:hidden items-center space-x-3">
                      {canWrite && (
                        <>
                          <button
                            onClick={() => handleEditNote(note)}
                            className="text-yellow-600 hover:text-yellow-800 transition-colors"
                            title="Edit Note"
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            disabled={deletingId === note.id}
                            className={`transition-colors ${deletingId === note.id ? "text-gray-400 cursor-not-allowed" : "text-red-500 hover:text-red-700"}`}
                            title="Delete Note"
                          >
                            <FaTrash size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {visibleRecordsCount < sortedNotes.length && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-2 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300 ease-in-out"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotesObservationsPage;
